import React, { useState } from 'react';
import { Interview } from '@/types/interview';
import PendingInterviewsTable from './PendingInterviewsTable';
import ViewInterviewDialog from './ViewInterviewDialog';
import EditInterviewDialog from './EditInterviewDialog';
import EmptyInterviewsList from './EmptyInterviewsList';
import { toast } from 'sonner';
import { InterviewService } from '@/services/interview/interviewService';
import { useQueryClient } from '@tanstack/react-query';
import ConfirmationDialog from './ConfirmationDialog';
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';

interface PendingInterviewsListProps {
  interviews: Interview[];
  onPublish: (interviewId: string) => Promise<void>;
  onReject: (interviewId: string) => Promise<void>;
}

const PendingInterviewsList: React.FC<PendingInterviewsListProps> = ({ 
  interviews, 
  onPublish,
  onReject
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [sendingToFeederId, setSendingToFeederId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const [isFeederConfirmOpen, setIsFeederConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [interviewToProcess, setInterviewToProcess] = useState<string | null>(null);

  const handleViewInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsViewDialogOpen(true);
  };

  const handleEditInterview = (interview: Interview) => {
    setSelectedInterview(interview);
    setIsEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setIsViewDialogOpen(false);
    setSelectedInterview(null);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedInterview(null);
  };

  const confirmPublish = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsPublishConfirmOpen(true);
  };

  const confirmSendToFeeder = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsFeederConfirmOpen(true);
  };

  const confirmDelete = (interviewId: string) => {
    setInterviewToProcess(interviewId);
    setIsDeleteConfirmOpen(true);
  };

  const handlePublish = async (interviewId: string) => {
    if (publishingId) {
      console.log(t('interviewManagement.messages.alreadyPublishing'));
      return;
    }
    
    try {
      console.log('PendingInterviewsList - Publiserer intervju:', interviewId);
      setPublishingId(interviewId);
      await onPublish(interviewId);
    } finally {
      setTimeout(() => setPublishingId(null), 500);
    }
  };

  const handleSendToFeeder = async (interviewId: string) => {
    if (sendingToFeederId) {
      console.log(t('interviewManagement.messages.alreadySending'));
      return;
    }
    
    try {
      console.log('PendingInterviewsList - Sender intervju til køen:', interviewId);
      setSendingToFeederId(interviewId);
      toast.loading(t('interviewManagement.messages.sendingToQueue'), { id: 'sending-to-feeder' });
      
      const result = await InterviewService.markForFeeder(interviewId);
      
      if (result) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.invalidateQueries({ queryKey: ['feederInterviews'] })
        ]);
        
        toast.success(t('interviewManagement.messages.sentToQueue'), { id: 'sending-to-feeder' });
        
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['pendingInterviews'] }),
          queryClient.refetchQueries({ queryKey: ['feederInterviews'] })
        ]);
      } else {
        toast.error(t('interviewManagement.messages.couldNotSendToQueue'), { id: 'sending-to-feeder' });
        throw new Error(t('interviewManagement.messages.couldNotSendToQueue'));
      }
    } catch (error) {
      console.error('Feil ved sending til køen:', error);
      toast.error(`${t('interviewManagement.messages.couldNotSendToQueue')}: ${error instanceof Error ? error.message : t('interviewManagement.messages.unknownError')}`, { id: 'sending-to-feeder' });
      throw error;
    } finally {
      setTimeout(() => setSendingToFeederId(null), 500);
    }
  };

  const handleDelete = async (interviewId: string) => {
    setIsDeleteConfirmOpen(false);
    setIsViewDialogOpen(false);
    setIsEditDialogOpen(false);
    
    if (deletingId) {
      console.log(t('interviewManagement.messages.alreadyDeleting'));
      return;
    }
    
    try {
      console.log('PendingInterviewsList - Deleting interview:', interviewId);
      
      setDeletingId(interviewId);
      
      toast.loading(t('interviewManagement.messages.deletingInterview'), { id: 'deleting-interview' });
      
      const result = await InterviewService.deleteInterview(interviewId);
      console.log('Deletion result:', result);
      
      if (result) {
        console.log('Delete successful, invalidating queries');
        
        await queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] });
        await queryClient.invalidateQueries({ queryKey: ['feederInterviews'] });
        await queryClient.invalidateQueries({ queryKey: ['interviews'] });
        
        toast.success(t('interviewManagement.messages.interviewDeleted'), { id: 'deleting-interview' });
        
        await queryClient.refetchQueries({ queryKey: ['pendingInterviews'] });
        await queryClient.refetchQueries({ queryKey: ['feederInterviews'] });
        await queryClient.refetchQueries({ queryKey: ['interviews'] });
      } else {
        console.error('Deletion returned false, interview may still exist');
        toast.error(`${t('interviewManagement.messages.couldNotDelete')} - returnerte false`, { id: 'deleting-interview' });
        
        try {
          console.log('Attempting alternate direct deletion method');
          const { error } = await supabase
            .from('interviews')
            .delete()
            .eq('id', interviewId);
            
          if (error) {
            console.error('Alternate deletion failed:', error);
            throw new Error(`Could not delete interview: ${error.message}`);
          } else {
            console.log('Alternate deletion may have succeeded, invalidating queries');
            await queryClient.invalidateQueries();
            toast.success(t('interviewManagement.messages.alternateDeleteSuccess'), { id: 'deleting-interview' });
          }
        } catch (altError) {
          console.error('Error in alternate deletion method:', altError);
          throw new Error(t('interviewManagement.messages.couldNotDelete'));
        }
      }
    } catch (error) {
      console.error('Error deleting interview:', error);
      toast.error(`${t('interviewManagement.messages.couldNotDelete')}: ${error instanceof Error ? error.message : t('interviewManagement.messages.unknownError')}`, { id: 'deleting-interview' });
    } finally {
      setDeletingId(null);
    }
  };

  if (interviews.length === 0) {
    return <EmptyInterviewsList />;
  }

  return (
    <div>
      <h3 className="text-lg font-medium mb-4">{t('interviewManagement.pendingCount', { count: interviews.length })}</h3>
      
      <PendingInterviewsTable 
        interviews={interviews}
        onViewInterview={handleViewInterview}
        onEditInterview={handleEditInterview}
        onPublish={confirmPublish}
        onSendToFeeder={confirmSendToFeeder}
        onDelete={confirmDelete}
        publishingId={publishingId}
        sendingToFeederId={sendingToFeederId}
        deletingId={deletingId}
      />

      <ViewInterviewDialog 
        interview={selectedInterview}
        isOpen={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        onPublish={handlePublish}
        onSendToFeeder={handleSendToFeeder}
        publishingId={publishingId}
        sendingToFeederId={sendingToFeederId}
      />

      <EditInterviewDialog 
        interview={selectedInterview}
        isOpen={isEditDialogOpen}
        onClose={handleCloseEditDialog}
      />

      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onClose={() => setIsPublishConfirmOpen(false)}
        onConfirm={async () => {
          if (interviewToProcess) {
            await handlePublish(interviewToProcess);
            setInterviewToProcess(null);
          }
          setIsPublishConfirmOpen(false);
        }}
        title={t('interviewManagement.confirmations.publishTitle')}
        description={t('interviewManagement.confirmations.publishDescription')}
        confirmText={t('interviewManagement.confirmations.confirm')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />

      <ConfirmationDialog
        isOpen={isFeederConfirmOpen}
        onClose={() => setIsFeederConfirmOpen(false)}
        onConfirm={async () => {
          if (interviewToProcess) {
            await handleSendToFeeder(interviewToProcess);
            setInterviewToProcess(null);
          }
          setIsFeederConfirmOpen(false);
        }}
        title={t('interviewManagement.confirmations.feederTitle')}
        description={t('interviewManagement.confirmations.feederDescription')}
        confirmText={t('interviewManagement.confirmations.confirm')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />
      
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          if (interviewToProcess) {
            handleDelete(interviewToProcess);
            setInterviewToProcess(null);
          }
        }}
        title={t('interviewManagement.confirmations.deleteTitle')}
        description={t('interviewManagement.confirmations.deleteDescription')}
        confirmText={t('interviewManagement.delete')}
        cancelText={t('interviewManagement.confirmations.cancel')}
      />
    </div>
  );
};

export default PendingInterviewsList;
